import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import * as PropTypes from 'prop-types';

import ButtonBase from '$app-web/ui-kit/components/button-base';
import ChatIconButton from '$app-web/ui-kit/components/chat-icon-button';
import { URLsManager } from '$app-web/utils';

import AvatarWithTitle from '../../components/avatar-with-title';
import { VOIP_CALL_STATUS, VOIP_CALL_TYPES } from '../../constants';
import {
    useChatFlags,
    useChatUserData,
    useCurrentStudyRooms,
    useCurrentStudyUsersData,
    useDynamicMethods,
    useMethods,
    useVoip,
} from '../../state/chat-client.state';
import classes from './styles.module.scss';

import { FontAwesomeIcon } from '$common/fontawesome-pro/react-fontawesome';

const Header = (props) => {
    const { avatar, title, direct, roomId, online } = props;

    // TODO: Define a Dropdown/Menu under the UI Kit and use that instead.
    const [isMembersDropdownOpen, setIsMembersDropdownOpen] = useState(false);

    // TODO: Use userDuck.duckSelectors.roleSelector and appUtils.user.isResearcher to show a tooltip on disabled
    //       voice/video call buttons instead of hiding those buttons if the corresponding features are disabled.
    //       We need to implement a Tooltip component when the design system is complete.

    const avatarWithTitleRef = useRef();

    const methods = useMethods();
    const voip = useVoip();
    const dynamicMethods = useDynamicMethods();
    const chatUserData = useChatUserData();
    const chatFlags = useChatFlags();
    const currentStudyUsersData = useCurrentStudyUsersData();
    const currentStudyRooms = useCurrentStudyRooms();

    const roomMembers = useMemo(() => dynamicMethods.getMembersDataByMatrixRoomId(roomId), [dynamicMethods, roomId]);

    const handleClickOutsideTitle = (event) => {
        if (avatarWithTitleRef.current && !avatarWithTitleRef.current.contains(event.target)) {
            setIsMembersDropdownOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutsideTitle, true);
        return () => {
            document.removeEventListener('click', handleClickOutsideTitle, true);
        };
    });

    const AvatarWithTitleWrapperComponent = direct ? 'div' : ButtonBase;

    const filteredRoomMembers = roomMembers.filter((member) => currentStudyUsersData[member.id] != null);

    return (
        <div className={clsx('item flex items-center content-center justify-between nowrap relative', classes.root)}>
            <div className="flex inline items-center">
                <ChatIconButton
                    icon={['fal', 'angle-left']}
                    className={clsx('item xs-auto md-0', classes.backButton)}
                    size="small"
                    color="secondary"
                    onClick={() => methods.setIsChatSidebarVisibleOnXs(true)}
                />
                <div className={classes.avatarWithTitleContainer} ref={avatarWithTitleRef}>
                    <AvatarWithTitleWrapperComponent onClick={() => setIsMembersDropdownOpen((preState) => !preState)}>
                        <AvatarWithTitle
                            avatarColor={avatar.color}
                            avatarLabel={avatar.label}
                            online={online}
                            src={avatar.url}
                            title={
                                <>
                                    {!direct && (
                                        <FontAwesomeIcon icon={['fas', 'user-friends']} className={classes.groupRoomIcon} />
                                    )}
                                    {title}
                                    {!direct && (
                                        <FontAwesomeIcon icon={['fal', 'angle-right']} className={classes.angleRightIcon} />
                                    )}
                                </>
                            }
                        />
                    </AvatarWithTitleWrapperComponent>
                </div>
            </div>
            {!direct && (
                <div className={clsx('scrollable hideX', classes.roomMembersList)} hidden={!isMembersDropdownOpen}>
                    {filteredRoomMembers.map((member) => {
                        const matchedAvatar = currentStudyUsersData[member.id];

                        return (
                            <ButtonBase
                                key={member.id}
                                className={clsx('flex nowrap items-center', classes.memberItem)}
                                disabled={chatUserData.userId.startsWith(`@u${member.id}:`)}
                                onClick={() => dynamicMethods.setCurrentRoomIdByUserId(member.id)}
                            >
                                <AvatarWithTitle
                                    avatarColor={matchedAvatar.color}
                                    avatarLabel={matchedAvatar.label}
                                    avatarSize="sm"
                                    classes={{ title: 'body1' }}
                                    online={currentStudyUsersData[member.id].isOnline}
                                    src={matchedAvatar.url && URLsManager.getURLTo(matchedAvatar.url)}
                                    title={currentStudyUsersData[member.id].label}
                                />
                            </ButtonBase>
                        );
                    })}
                </div>
            )}
            {/* If direct room, show the VoIP buttons */}
            {direct &&
                !currentStudyRooms[roomId].isArchived &&
                (chatFlags.isVideoChatEnabledForCurrentStudy || chatFlags.isVoiceChatEnabledForCurrentStudy) && (
                    <div className="item">
                        <div className="flex space-1">
                            {chatFlags.isVoiceChatEnabledForCurrentStudy && (
                                <div className="item">
                                    <ChatIconButton
                                        className={classes.iconButton}
                                        icon={['fad', 'phone-alt']}
                                        color="secondary"
                                        onClick={() => methods.createVoipCallInRoom(roomId, VOIP_CALL_TYPES.VOICE)}
                                        disabled={
                                            voip.incomingCallsData.length > 0 ||
                                            voip.activeCallData.status !== VOIP_CALL_STATUS.UNKNOWN
                                        }
                                    />
                                </div>
                            )}
                            {chatFlags.isVideoChatEnabledForCurrentStudy && (
                                <div className="item">
                                    <ChatIconButton
                                        className={classes.iconButton}
                                        icon={['fad', 'video']}
                                        color="secondary"
                                        onClick={() => methods.createVoipCallInRoom(roomId, VOIP_CALL_TYPES.VIDEO)}
                                        disabled={
                                            voip.incomingCallsData.length > 0 ||
                                            voip.activeCallData.status !== VOIP_CALL_STATUS.UNKNOWN
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
        </div>
    );
};

Header.defaultProps = {
    roomId: undefined,
    avatar: {},
    online: undefined,
};

Header.propTypes = {
    title: PropTypes.string.isRequired,
    direct: PropTypes.bool.isRequired,
    roomId: PropTypes.string,
    avatar: PropTypes.oneOfType([PropTypes.object]),
    online: PropTypes.bool,
};

export default Header;
